import React from "react";
import locale from "@bonliva-crm/locale";
import { BulkActionSubType, SelectableBulkActionType } from "@bonliva-crm/shared";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip
} from "@material-ui/core";
import { BulkActionModalContents } from "./components/bulk-action-modal-contents";
import { ExternalSelectionStepProps } from "./components/selection-step";
import { useBulkActionData } from "./hooks/use-bulk-action-data";
import { ExternalActionStepProps } from "./utils/action-step-props";
import { SelectionStepRowType } from "./utils/selection-step-row-type";
import { Steps } from "./utils/step";

type Props<
  RowType extends SelectionStepRowType,
  ActionType extends SelectableBulkActionType
> = {
  type: ActionType;
  subType: (typeof BulkActionSubType)[ActionType];
  stepProps: {
    [Steps.Selection]: ExternalSelectionStepProps<RowType>;
    [Steps.Action]: ExternalActionStepProps<RowType>;
  };
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const BulkActionModal = <
  RowType extends SelectionStepRowType,
  ActionType extends SelectableBulkActionType
>(
  props: Props<RowType, ActionType>
) => {
  const {
    currentStep,
    setCurrentStep,
    stepData,
    resetValues,
    selectedRows,
    setSelectedRows,
    bulkActionData
  } = useBulkActionData({
    type: props.type,
    subType: props.subType,
    stepProps: props.stepProps
  });

  const current = stepData[currentStep];
  const previousStep = current.previousStep;
  const nextStep = current.nextStep;

  return (
    <Dialog
      open={props.open}
      onClose={() => {
        resetValues();
        props.setOpen(false);
      }}
      maxWidth="xl"
      fullWidth
    >
      <DialogTitle>
        {`${locale.MassActions} ${locale.bulkActionType[props.type]} ${
          props.subType
            ? `- ${locale.bulkActionSubType[props.subType].toLowerCase()}`
            : ""
        }`}
      </DialogTitle>

      <DialogContent>
        <BulkActionModalContents
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          disabledSteps={{
            Selection: false,
            Action: stepData[Steps.Selection].nextStepDisabled,
            Confirmation:
              stepData[Steps.Selection].nextStepDisabled ||
              stepData[Steps.Action].nextStepDisabled
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          stepProps={props.stepProps}
          type={props.type}
          subType={props.subType}
          bulkActionData={bulkActionData}
        />
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            resetValues();
            props.setOpen(false);
          }}
        >
          {locale.Cancel}
        </Button>

        <Button
          variant="outlined"
          color="primary"
          onClick={() => setCurrentStep(previousStep)}
          disabled={!previousStep}
        >
          {locale.previous}
        </Button>

        <Tooltip
          placement="top"
          title={current.nextStepDisabled ? current.nextStepDisabledToolTip : ""}
        >
          {nextStep ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setCurrentStep(nextStep)}
              disabled={current.nextStepDisabled}
            >
              {locale.Next}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={current.nextStepDisabled}
              onClick={() => {
                bulkActionData[props.type].onSubmit();
                resetValues();
                props.setOpen(false);
              }}
            >
              {locale.Confirm}
            </Button>
          )}
        </Tooltip>
      </DialogActions>
    </Dialog>
  );
};
